import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader, Pager } from '@/components/kit';
import { FilterSelect, ListBody, SearchBox, Toolbar, useDebounced } from '@/components/inputs';
import DataTable, { Dash, RowLink } from '@/components/admin/DataTable';
import { useCustomerCosts } from '@/components/admin/OrderCosts';
import { CUSTOMER_TAGS } from '@/shared/constants';
import { CUSTOMER_FIELDS } from '@/shared/fields';
import { credits, signedMoney } from '@/shared/ledger';
import { paged, toArray } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

const { or, contains, includes } = window.ApperSDK.CoreSDK;

export const route = { path: '/admin/customers', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };
export const nav = { icon: 'Users', label: 'Customers', section: 'Store', profiles: ['Admin_c', 'Staff_c'], order: 30 };

const PAGE_SIZE = 20;

export default function AdminCustomers() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [tag, setTag] = useState('all');
  const [page, setPage] = useState(1);
  const q = useDebounced(search.trim());

  useEffect(() => {
    setPage(1);
  }, [q, tag]);

  const { data, loading, error, run } = useFetch(() => {
    let query = sdk.table('customers_c').select(CUSTOMER_FIELDS);
    if (q) query = query.where(or(contains('Name', q), contains('email_c', q), contains('contact_handle_c', q)));
    if (tag !== 'all') query = query.where(includes('tags_c', tag));
    return query.orderByDesc('CreatedOn').page(page, PAGE_SIZE).fetch().then(paged);
  }, [q, tag, page]);

  const list = data?.rows ?? [];
  const filtered = !!q || tag !== 'all';
  const costs = useCustomerCosts(list.map((c) => c.Id));

  function clear() {
    setSearch('');
    setTag('all');
  }

  const columns = [
    {
      label: 'Customer',
      render: (c) => (
        <RowLink to={`/admin/customers/${c.Id}`} sub={c.email_c}>
          {c.Name}
        </RowLink>
      ),
    },
    {
      label: 'Contact',
      className: 'hidden md:table-cell',
      render: (c) =>
        c.contact_handle_c ? (
          <span>
            {c.contact_handle_c}
            <span className="block text-xs text-muted-foreground">{c.contact_method_c}</span>
          </span>
        ) : (
          <Dash />
        ),
    },
    {
      label: 'Tags',
      className: 'hidden lg:table-cell',
      render: (c) => {
        const tags = toArray(c.tags_c);
        if (!tags.length) return <Dash />;
        return (
          <span className="flex flex-wrap gap-1">
            {tags.map((t) => (
              <Badge key={t} variant="outline">
                {t}
              </Badge>
            ))}
          </span>
        );
      },
    },
    {
      label: 'Credits / cost',
      className: 'hidden sm:table-cell',
      render: (c) => {
        const t = costs.data?.[c.Id];
        if (costs.loading && !costs.data) return <Skeleton className="h-8 w-20" />;
        if (!t?.orders) return <span className="text-xs text-muted-foreground">No orders</span>;
        if (!t.typed) {
          return (
            <Link to={`/admin/customers/${c.Id}`} className="text-xs text-muted-foreground hover:text-foreground">
              Add credits
            </Link>
          );
        }
        return (
          <Link to={`/admin/customers/${c.Id}`} className="block tabular-nums hover:text-primary">
            <span className="block text-sm">{credits(t.credits)} cr</span>
            <span className="block text-xs text-muted-foreground">
              {signedMoney(t.cost)} cost · {t.orders} order{t.orders === 1 ? '' : 's'}
            </span>
          </Link>
        );
      },
    },
    {
      label: 'Promos',
      className: 'hidden md:table-cell',
      render: (c) =>
        c.unsubscribed_at_c ? (
          <span className="text-muted-foreground">Unsubscribed</span>
        ) : c.marketing_opt_in_c ? (
          <span className="text-success">Opted in</span>
        ) : (
          <span className="text-muted-foreground">No</span>
        ),
    },
    {
      label: 'Joined',
      className: 'hidden sm:table-cell text-right text-muted-foreground',
      render: (c) => formatLocalDate(c.CreatedOn, DATE_FORMATS.SHORT),
    },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow="Store"
        title="Customers"
        description="Everyone who has ordered or signed up, with how to reach them and what their orders cost you."
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/admin/orders/import">
                <ApperIcon name="Upload" size={16} />
                Import clients
              </Link>
            </Button>
            <Button asChild>
              <Link to="/admin/customers/new">
                <ApperIcon name="UserPlus" size={16} />
                New customer
              </Link>
            </Button>
          </>
        }
      />

      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Search name, email or handle…" />
        <FilterSelect value={tag} onChange={setTag} values={CUSTOMER_TAGS} allLabel="All tags" />
      </Toolbar>

      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        isEmpty={!list.length}
        empty={{
          icon: 'Users',
          title: filtered ? 'No customers match' : 'No customers yet',
          description: filtered
            ? 'Try another search or tag.'
            : 'Customers appear when they sign up, you add them, or you import a client list.',
          action: filtered ? clear : () => navigate('/admin/customers/new'),
          actionLabel: filtered ? 'Clear filters' : 'New customer',
        }}
      >
        <DataTable columns={columns} rows={list} />
        <Pager page={page} total={data?.total} pageSize={PAGE_SIZE} hasMore={data?.hasMore} onPage={setPage} />
      </ListBody>
    </div>
  );
}
